import React from "react";
import { sortBy, memoize } from "lodash";
import { Flex, Box, Golden as GoldenBox } from "grid-styled";
import { Route } from "react-router-dom";
import queryString from "query-string";
import { gql } from "apollo-boost";
import { graphql, compose } from "react-apollo";
import moment from "moment";
import { connect } from "react-redux";
import Icon from "../Icon";
import Button from "../Button";
import Modal from "../Modal";
import CenteredLoader from "../CenteredLoader";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverContentItem
} from "../Popover";
import {
  addUpload,
  startUpload,
  deleteUpload,
  cancelAllPendingUploads,
  getUploadsByStatus,
  PENDING,
  UPLOADING
} from "../../ducks/uploads";
import { TabBar, TabLink } from "../tabs";
import ActivityFeed from "../activities/ActivityFeed";
import Attachments from "../Attachments";
import ServiceSwitcher from "./ServiceSwitcher";
import ServiceSidebar from "./ServiceSidebar";
import ServiceOptionsForm from "./ServiceOptionsForm";
import ServiceQuery from "../../queries/Service";
import ServiceAttachmentsQuery, {
  fragments
} from "../../queries/ServiceAttachments";
import ChangeStatusForm from "../ChangeStatusForm";
import JobQuery from "../../queries/Job";
import JobServicesQuery from "../../queries/JobServices";
import withCurrentUser from "../../hocs/withCurrentUser";
import JobContractsQuery from "../../queries/JobContracts";
import ChangeStatusMutation from "../../mutations/ChangeStatus";
import ConvertServiceMutation from "../../mutations/ConvertToNewService";
import CreateContractMutation from "../../mutations/CreateContract";
import UpdateContractMutation from "../../mutations/UpdateContract";
import UpdateJobMutation from "../../mutations/UpdateJob";
import TransmittalEditor from "../TransmittalEditor";
import UploadsModal from "../UploadsModal";
import { getTransmittalTemplates } from "../../api";
import { ADD } from "../../icons";
import { ATTACHMENT_TYPES, REQUIRED_ATTACHMENTS } from "../../constants";
import { addToCache, deleteFromCache } from "../../utils/queryHelpers";
import { amountToCents } from "../../utils/helpers";
import { setPreferences, getPreferences } from "../../utils/localStorage";
import join from "../../utils/join-path";

const preferenceKey = serviceId => `services:${serviceId}`;

class ServiceHistory extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      currentTab: "status",
      loadingTemplates: false,
      templates: [],
      clickedType: null,
      showingStatusChangeModal: false,
      showingSubmitModal: false,
      showingRescindModal: false,
      showingConvertModal: false,
      ...getPreferences(preferenceKey(props.match.params.serviceId))
    };
  }

  componentDidMount() {
    this.fetchTemplates();
  }

  getDropTypes = memoize(user => {
    return ATTACHMENT_TYPES.filter(type => {
      return !type.allowDropFrom || type.allowDropFrom.indexOf(user.role) > -1;
    });
  }, user => user && user.role);

  getAttachmentRequirements = memoize(
    (attachmentData, service) => {
      const { loading, error, attachments } = attachmentData;
      const { serviceType, options } = service;

      const formatRequirement = requirement => {
        const complete =
          !loading &&
          !error &&
          attachments.some(
            attachment =>
              attachment.attachmentType === requirement.attachmentType
          );

        return {
          complete,
          message: complete ? requirement.complete : requirement.incomplete,
          type: requirement.attachmentType
        };
      };

      return REQUIRED_ATTACHMENTS[serviceType]
        .filter(r => (r.required ? r.required(options) : true))
        .map(formatRequirement);
    },
    (attachmentData, services) => JSON.stringify({ attachmentData, services })
  );

  fetchTemplates = () => {
    this.setState({
      loadingTemplates: true
    });
    getTransmittalTemplates()
      .then(templates => this.setState({ templates, loadingTemplates: false }))
      .catch(err => {
        this.setState({ loadingTemplates: false });
        console.error("Error fetching templates", err);
      });
  };

  addFiles = (files, type = "") => {
    const { serviceId } = this.props.match.params;
    for (var i = 0; i < files.length; i++) {
      const file = files[i];
      this.props.addUpload({
        file,
        serviceId,
        attachmentType: type
      });
    }
  };

  handleTabClick = currentTab => () => {
    const { serviceId } = this.props.match.params;
    setPreferences(preferenceKey(serviceId), { currentTab });
    this.setState({ currentTab });
  };

  handleAttachmentSelection = value => {
    value();
  };

  handleUploadButtonClick = e => {
    if (e) e.preventDefault();
    this.setState({ clickedType: null });
    this.fileUploadField.click();
  };

  handleSectionClick = type => e => {
    if (e) e.preventDefault();
    this.setState({ clickedType: type });
    this.fileUploadField.click();
  };

  handleFilePick = e => {
    const { clickedType } = this.state;
    if (e && e.target) {
      this.addFiles(e.target.files, clickedType);
      this.setState({ clickedType: null });
    }
  };

  closeModals = () => {
    this.setState({
      showingStatusChangeModal: false,
      showingSubmitModal: false,
      showingRescindModal: false
    });
  };

  renderAdminStatusChangeBox() {
    const service = this.props.serviceData.service;

    return (
      <Box pt={0} pb={2}>
        <Button
          onClick={() => this.setState({ showingStatusChangeModal: true })}
        >
          Change Status
        </Button>
        <Modal
          title="Changing Service Status..."
          isOpen={this.state.showingStatusChangeModal}
          close={this.closeModals}
        >
          <Box p={2}>
            <ChangeStatusForm
              cancel={this.closeModals}
              service={service}
              onSubmit={this.closeModals}
            />
          </Box>
        </Modal>
      </Box>
    );
  }

  renderFranchiseeStatusChangeBox() {
    const { service } = this.props.serviceData;
    if (!service) return null;

    const status = service.status;
    const requirements = this.getAttachmentRequirements(
      this.props.attachmentData,
      service
    );

    // Submit for Review
    if (status === "draft") {
      const submit = () =>
        this.props.submitForReview(service).then(this.closeModals);
      return (
        <Box pt={0} pb={2}>
          <Button
            onClick={() => this.setState({ showingSubmitModal: true })}
            disabled={!requirements.every(r => r.complete)}
          >
            Submit For Review
          </Button>
          <Modal
            title="Submitting Service Request..."
            isOpen={this.state.showingSubmitModal}
            close={this.closeModals}
          >
            <Box p={2}>
              <p>Are you sure you want to submit this service request?</p>
              {/* TODO: Warn if items are missing */}
              <Flex justify="flex-end">
                <Button type="button" onClick={this.closeModals}>
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  style={{ marginLeft: 4 }}
                  onClick={submit}
                >
                  Submit For Review
                </Button>
              </Flex>
            </Box>
          </Modal>
        </Box>
      );
    }

    // Rescind from Review
    if (status === "awaiting_review") {
      const rescind = () =>
        this.props.pullFromReview(service).then(this.closeModals);
      return (
        <Box pt={0} pb={2}>
          <Button onClick={() => this.setState({ showingRescindModal: true })}>
            Pull From Review Queue
          </Button>
          <Modal
            title="Pulling From Review Queue"
            isOpen={this.state.showingRescindModal}
            close={this.closeModals}
          >
            <Box p={2}>
              <p>
                Are you sure you want to pull this service request from the
                review queue?
              </p>
              {/* TODO: Warn if items are missing */}
              <Flex justify="flex-end">
                <Button type="button" onClick={this.closeModals}>
                  Cancel
                </Button>
                <Button
                  type="button"
                  danger
                  style={{ marginLeft: 4 }}
                  onClick={rescind}
                >
                  Pull From Review
                </Button>
              </Flex>
            </Box>
          </Modal>
        </Box>
      );
    }

    // Sales Drawing Conversion
    if (service.serviceType === "Sales Drawing" && status === "closed") {
      const convert = values => {
        this.props
          .convertToNewService(service, { serviceType: "New Sale", ...values })
          .then(({ data: { convertToNewService: service } }) => {
            this.setState({ showingConvertModal: false });
            this.props.history.push(`${this.props.baseUrl}/${service.id}`);
          });
      };

      return (
        <Box pt={0} pb={2}>
          <Button
            primary
            onClick={() => this.setState({ showingConvertModal: true })}
          >
            Convert To New Sale
          </Button>
          <Modal
            title="Converting Sales Drawing..."
            isOpen={this.state.showingConvertModal}
            close={this.closeModals}
            style={{ maxWidth: 600 }}
          >
            <Box p={2}>
              <ServiceOptionsForm
                cancel={this.closeModals}
                service={{ ...service, serviceType: "New Sale" }}
                onSubmit={convert}
              />
            </Box>
          </Modal>
        </Box>
      );
    }
  }

  renderSidebar() {
    const { serviceData, attachmentData } = this.props;
    const { loading, error, service } = serviceData;

    if (loading && !service) return <CenteredLoader />;
    if (error) return "Error.";

    const attachmentRequirements = this.getAttachmentRequirements(
      attachmentData,
      service
    );

    switch (this.state.currentTab) {
      case "status":
        return (
          <ServiceSidebar
            service={service}
            attachmentRequirements={attachmentRequirements}
          />
        );
      case "attachments":
        return (
          <Attachments
            addFiles={this.addFiles}
            deleteAttachment={this.props.deleteAttachment}
            jobId={this.props.match.params.jobId}
            dropTypes={this.getDropTypes(this.props.userData.currentUser)}
            handleSectionClick={this.handleSectionClick}
            transmittalTemplates={this.state.templates}
            data={attachmentData}
            requirements={attachmentRequirements}
          />
        );
      default:
        return null;
    }
  }

  newTransmittal = templateId => {
    this.props.history.push(
      join(this.props.match.url, `/transmittals/new?template-id=${templateId}`)
    );
  };

  renderAttachmentButton = () => {
    const { templates } = this.state;
    const sortedTemplates = sortBy(templates, "name");
    return (
      <Popover
        onSelection={this.handleAttachmentSelection}
        style={{ position: "absolute", right: 0, top: 8 }}
      >
        <PopoverTrigger>
          <Icon icon={ADD} />
        </PopoverTrigger>
        <PopoverContent>
          {sortedTemplates.map(template => (
            <PopoverContentItem
              key={template.id}
              value={() => this.newTransmittal(template.id)}
            >
              New {template.name}
            </PopoverContentItem>
          ))}
        </PopoverContent>
      </Popover>
    );
  };

  renderTransmittalEditor = () => {
    return (
      <Route
        path={`${this.props.match.path}/transmittals/:transmittalId`}
        render={({ match, location, history }) => {
          const query = queryString.parse(location.search);
          return (
            <TransmittalEditor
              admin={match.path.indexOf("admin") > -1}
              serviceId={match.params.serviceId}
              templateId={query["template-id"]}
              transmittalId={match.params.transmittalId}
              close={() => history.push(this.props.match.url)}
            />
          );
        }}
      />
    );
  };

  renderUploadsModal = () => (
    <UploadsModal
      dropTypes={this.getDropTypes(this.props.userData.currentUser)}
      job={this.props.jobData.job}
      uploads={Object.values(this.props.pendingUploads)}
      start={this.handleUploadStart}
      cancel={this.props.deleteUpload}
      cancelAll={this.props.cancelAllPendingUploads}
    />
  );

  handleUploadStart = upload => {
    this.props
      .startUpload(upload)
      .then(response => {
        if (response && upload.attachmentType === "CONTRACT") {
          const { attachment } = JSON.parse(response);
          if (upload.contractId === "new") {
            return Promise.all([
              this.props.createContract({
                input: {
                  attachmentId: attachment.id,
                  amountInCents: amountToCents(upload.contractAmount),
                  effectiveDate: upload.contractEffectiveDate
                }
              }),
              this.props.updateJob({
                input: {
                  contractNumber: upload.contractNumber,
                  contractType: upload.contractType
                }
              })
            ]);
          } else {
            this.props.updateContract({
              id: upload.contractId,
              input: { attachmentId: attachment.id }
            });
          }
        }

        return response;
      })
      .then(() => this.props.attachmentData.refetch());
  };

  render() {
    const { services, baseUrl } = this.props;
    const isAdmin = baseUrl.match(/^\/admin/);

    return (
      <Flex>
        <GoldenBox.A pr={2}>
          <Flex justify="space-between" align="center">
            <ServiceSwitcher services={services} />
          </Flex>
          <ActivityFeed />
        </GoldenBox.A>
        <GoldenBox.B pl={2}>
          <input
            ref={field => (this.fileUploadField = field)}
            type="file"
            multiple
            onChange={this.handleFilePick}
            style={{ display: "none" }}
          />
          {isAdmin
            ? this.renderAdminStatusChangeBox()
            : this.renderFranchiseeStatusChangeBox()}
          <TabBar bordered style={{ paddingTop: 0, position: "relative" }}>
            <TabLink
              component="a"
              onClick={this.handleTabClick("status")}
              active={this.state.currentTab === "status"}
            >
              Status
            </TabLink>
            <TabLink
              component="a"
              onClick={this.handleTabClick("attachments")}
              active={this.state.currentTab === "attachments"}
            >
              Attachments
            </TabLink>
            {this.renderAttachmentButton()}
          </TabBar>
          {this.renderSidebar()}
        </GoldenBox.B>
        {this.renderTransmittalEditor()}
        {this.renderUploadsModal()}
      </Flex>
    );
  }
}

const DeleteAttachmentMutation = gql`
  mutation DeleteAttachmentMutation($id: ID!) {
    deleteAttachment(id: $id) {
      ...ServiceAttachmentsAttachment
    }
  }
  ${fragments.attachment}
`;

const enhance = compose(
  connect(
    state => ({
      pendingUploads: getUploadsByStatus(state.uploads, PENDING),
      activeUploads: getUploadsByStatus(state.uploads, UPLOADING)
    }),
    {
      addUpload,
      startUpload,
      deleteUpload,
      cancelAllPendingUploads
    }
  ),
  withCurrentUser(),
  graphql(ServiceQuery, {
    name: "serviceData",
    options: ({ match: { params: { serviceId } } }) => ({
      variables: { id: serviceId },
      pollInterval: 1000 * 30,
      fetchPolicy: "network-only"
    })
  }),
  graphql(ServiceAttachmentsQuery, {
    name: "attachmentData",
    options: ({ match: { params: { serviceId } } }) => ({
      variables: { serviceId },
      pollInterval: 1000 * 30,
      fetchPolicy: "network-only"
    })
  }),
  graphql(JobQuery, {
    name: "jobData",
    options: ({ match: { params: { jobId } } }) => ({
      variables: { jobId }
    })
  }),
  graphql(UpdateJobMutation, {
    props: ({ ownProps: { match: { params: { jobId } } }, mutate }) => ({
      updateJob({ input }) {
        return mutate({
          variables: {
            id: jobId,
            input
          }
        });
      }
    })
  }),
  graphql(CreateContractMutation, {
    props: ({ ownProps: { match: { params: { jobId } } }, mutate }) => ({
      createContract({ input }) {
        return mutate({
          variables: {
            input: {
              ...input,
              amountInCents: +input.amountInCents,
              effectiveDate: moment(input.effectiveDate).format("YYYY-MM-DD"),
              jobId
            }
          },
          refetchQueries: [
            {
              query: JobQuery,
              variables: { jobId }
            }
          ],
          update: (store, { data: { createContract } }) => {
            addToCache(createContract, store, "contracts", {
              query: JobContractsQuery,
              variables: { jobId }
            });
          }
        });
      }
    })
  }),
  graphql(UpdateContractMutation, {
    props: ({ mutate }) => ({
      updateContract(variables) {
        return mutate({ variables });
      }
    })
  }),
  graphql(DeleteAttachmentMutation, {
    props: ({ ownProps: { match: { params: { serviceId } } }, mutate }) => {
      return {
        deleteAttachment({ id }) {
          return mutate({
            variables: { id },
            update: (store, result) => {
              deleteFromCache(id, store, "attachments", {
                query: ServiceAttachmentsQuery,
                variables: { serviceId }
              });
            }
          });
        }
      };
    }
  }),
  graphql(ChangeStatusMutation, {
    props: ({ mutate }) => ({
      submitForReview(service) {
        return mutate({
          variables: {
            id: service.id,
            input: {
              status:
                service.serviceType === "Sales Drawing"
                  ? "open"
                  : "awaiting_review"
            }
          }
        });
      },
      pullFromReview(service) {
        return mutate({
          variables: {
            id: service.id,
            input: {
              status: "draft"
            }
          }
        });
      }
    })
  }),
  graphql(ConvertServiceMutation, {
    props: ({ ownProps, mutate }) => ({
      convertToNewService(service, input) {
        return mutate({
          variables: {
            id: service.id,
            input
          },
          update: (store, { data: { convertToNewService } }) => {
            addToCache(convertToNewService, store, "services", {
              query: JobServicesQuery,
              variables: { jobId: ownProps.match.params.jobId }
            });
          }
        });
      }
    })
  })
);

export default enhance(ServiceHistory);



// WEBPACK FOOTER //
// ./src/components/services/ServiceHistory.js